import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { HELP_SECTIONS } from './sections';
import { findSpans, locateOffset, makeSnippet, normalizeWithMap, searchPlans } from './helpSearch';

/**
 * 사용설명서 검색 — 렌더된 본문 DOM을 읽어 찾고, CSS Custom Highlight API로 강조한다.
 *
 * 본문은 데이터가 아니라 JSX이고 일부 숫자(첨부 용량·차단 확장자)는 런타임에 렌더되므로,
 * 검색용 텍스트를 따로 두면 조용히 어긋난다. 그래서 검색할 때마다 화면에 있는 그대로를 읽는다.
 * ⚠ 강조를 `<mark>`로 감싸지 말 것 — React가 소유한 DOM을 바꾸면 재렌더 때 깨진다.
 * Highlight API가 없는 환경(iOS 17.2 미만 등)은 강조 없이 결과 목록·이동만 동작한다.
 *
 * 결과 단위(블록): 장 머리글 / 소제목(`<Sub>` = h3)부터 다음 소제목 전까지 / FAQ 질문 하나
 * (`data-help-block`). 장 Body가 fragment라 소제목이 `<section>`의 직계 자식이라는 점에 기댄다.
 */

export interface HelpSearchResult {
  key: string;
  sectionId: string;
  /** 소제목 · FAQ 질문 · (장 머리글이면) 장 제목 */
  heading: string;
  isChapter: boolean;
  snippet: string;
  /** hits 배열에서 이 블록의 첫 매치 인덱스 */
  firstHit: number;
  hitCount: number;
}

/** 텍스트 노드를 문단 단위로 묶는다 — 이 경계를 넘어서는 매칭하지 않는다(표 칸끼리 붙어 오탐되는 것 방지) */
const UNIT_SELECTOR = 'p, li, td, th, h2, h3, div';
/** 한 글자 검색어로 수천 개가 걸리는 것 대비 */
const MAX_HITS = 1000;
const SEARCH_DEBOUNCE_MS = 150;
const MUTATION_DEBOUNCE_MS = 200;
const HL_ALL = 'help-search';
const HL_CURRENT = 'help-search-current';

interface Unit {
  nodes: Text[];
  starts: number[];
  text: string;
}

interface Block {
  sectionId: string;
  heading: string;
  isChapter: boolean;
  /** 첫 Unit이 제목(h2·h3·FAQ 질문)인지 — 스니펫은 제목이 아닌 문단에서 뽑는다 */
  hasHeadingUnit: boolean;
  els: Element[];
}

interface SearchState {
  /** 실제로 결과를 낸 계획의 낱말 — 결과 목록의 강조도 이것으로 칠한다 */
  terms: string[][];
  results: HelpSearchResult[];
  hits: Range[];
  /** hits[i]가 속한 results 인덱스 */
  hitResult: number[];
  truncated: boolean;
}

const EMPTY: SearchState = { terms: [], results: [], hits: [], hitResult: [], truncated: false };

function collectBlocks(root: HTMLElement): Block[] {
  const blocks: Block[] = [];
  Array.from(root.children).forEach((section) => {
    if (section.tagName !== 'SECTION' || !section.id.startsWith('help-')) return;
    const sectionId = section.id.slice('help-'.length);
    const title = HELP_SECTIONS.find((s) => s.id === sectionId)?.title ?? '';
    let lastSub = title;
    let current: Block | null = null;
    const start = (heading: string, isChapter: boolean, hasHeadingUnit: boolean): Block => {
      const block: Block = { sectionId, heading, isChapter, hasHeadingUnit, els: [] };
      blocks.push(block);
      return block;
    };

    Array.from(section.children).forEach((el, i) => {
      if (i === 0) {
        // 장 머리글(아이콘 + 제목 + 요약). 첫 소제목 전의 도입 문단도 여기에 붙는다
        current = start(title, true, true);
      } else if (el.tagName === 'H3') {
        lastSub = (el.textContent || '').trim();
        current = start(lastSub, false, true);
      } else if (el.hasAttribute('data-help-block')) {
        start(el.getAttribute('data-help-title') || lastSub, false, true).els.push(el);
        current = null;
        return;
      } else if (!current) {
        // FAQ 항목 뒤에 이어지는 일반 문단 — 직전 소제목 이름으로 새 블록
        current = start(lastSub, false, false);
      }
      (current as Block).els.push(el);
    });
  });
  return blocks;
}

function collectUnits(els: Element[]): Unit[] {
  const units: Unit[] = [];
  let cur: Unit | null = null;
  let curContainer: Element | null = null;
  els.forEach((el) => {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) {
      if (!n.data.length) continue;
      const container = n.parentElement?.closest(UNIT_SELECTOR) ?? el;
      if (!cur || container !== curContainer) {
        cur = { nodes: [], starts: [], text: '' };
        units.push(cur);
        curContainer = container;
      }
      cur.starts.push(cur.text.length);
      cur.nodes.push(n);
      cur.text += n.data;
    }
  });
  return units;
}

function toRange(unit: Unit, start: number, end: number): Range {
  const [sn, so] = locateOffset(unit.starts, start, false);
  const [en, eo] = locateOffset(unit.starts, end, true);
  const range = document.createRange();
  range.setStart(unit.nodes[sn], so);
  range.setEnd(unit.nodes[en], eo);
  return range;
}

function runSearch(root: HTMLElement, terms: string[][]): SearchState {
  if (!terms.length) return EMPTY;
  const results: HelpSearchResult[] = [];
  const hits: Range[] = [];
  const hitResult: number[] = [];
  let truncated = false;

  for (const block of collectBlocks(root)) {
    const units = collectUnits(block.els);
    const matched = terms.map(() => false);
    const found: Array<{ ui: number; spans: Array<[number, number]> }> = [];
    units.forEach((unit, ui) => {
      const { norm, map } = normalizeWithMap(unit.text);
      const r = findSpans(norm, map, terms);
      r.matched.forEach((m, ti) => {
        if (m) matched[ti] = true;
      });
      if (r.spans.length) found.push({ ui, spans: r.spans });
    });
    // 여러 낱말은 AND — 같은 블록 어딘가에 전부 있어야 한다
    if (!matched.every(Boolean)) continue;

    const bodyStart = block.hasHeadingUnit ? 1 : 0;
    const bodyHit = found.find((f) => f.ui >= bodyStart);
    const snippet = bodyHit
      ? makeSnippet(units[bodyHit.ui].text, bodyHit.spans[0][0], bodyHit.spans[0][1])
      : units[bodyStart]
        ? makeSnippet(units[bodyStart].text, 0, 0, 0, 72)
        : '';

    const firstHit = hits.length;
    const resultIndex = results.length;
    for (const { ui, spans } of found) {
      for (const [s, e] of spans) {
        if (hits.length >= MAX_HITS) {
          truncated = true;
          break;
        }
        hits.push(toRange(units[ui], s, e));
        hitResult.push(resultIndex);
      }
      if (truncated) break;
    }
    if (hits.length > firstHit) {
      results.push({
        key: `${block.sectionId}:${resultIndex}`,
        sectionId: block.sectionId,
        heading: block.heading,
        isChapter: block.isChapter,
        snippet,
        firstHit,
        hitCount: hits.length - firstHit,
      });
    }
    if (truncated) break;
  }
  return { terms, results, hits, hitResult, truncated };
}

/** 계획을 차례로 시도해 결과가 나온 첫 계획을 쓴다(구절 → 낱말별 AND, helpSearch.searchPlans) */
function runPlans(root: HTMLElement, plans: string[][][]): SearchState {
  let last = EMPTY;
  for (const terms of plans) {
    last = runSearch(root, terms);
    if (last.results.length) return last;
  }
  return last;
}

// TS 4.9의 lib.dom에는 Highlight 타입이 없을 수 있어 필요한 만큼만 선언한다
type HighlightLike = { priority?: number };
type HighlightCtor = new (...ranges: Range[]) => HighlightLike;
interface HighlightRegistryLike {
  set(name: string, highlight: HighlightLike): void;
  delete(name: string): boolean;
}

function paintHighlight(name: string, ranges: Range[], priority = 0) {
  const registry =
    typeof CSS !== 'undefined' ? (CSS as unknown as { highlights?: HighlightRegistryLike }).highlights : undefined;
  const Ctor = (window as unknown as { Highlight?: HighlightCtor }).Highlight;
  if (!registry || !Ctor) return;
  if (!ranges.length) {
    registry.delete(name);
    return;
  }
  const highlight = new Ctor(...ranges);
  highlight.priority = priority;
  registry.set(name, highlight);
}

export function useHelpSearch(
  contentRef: RefObject<HTMLElement>,
  scrollRef: RefObject<HTMLElement>,
  query: string
) {
  const [debounced, setDebounced] = useState('');
  const [state, setState] = useState<SearchState>(EMPTY);
  const [current, setCurrent] = useState(0);
  // 내용 기준으로 고정 — "알림" 뒤에 띄어쓰기만 쳤을 때 다시 찾아 첫 매치로 튀지 않게
  const plansKey = useMemo(() => JSON.stringify(searchPlans(debounced)), [debounced]);
  const plans = useMemo(() => JSON.parse(plansKey) as string[][][], [plansKey]);

  // 지울 때는 즉시, 칠 때는 잠깐 기다렸다가
  useEffect(() => {
    if (!query.trim()) {
      setDebounced('');
      return;
    }
    const t = window.setTimeout(() => setDebounced(query), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query]);

  /** 매치가 스크롤 영역 위에서 30% 지점에 오게 본문을 민다 */
  const scrollToRange = useCallback(
    (range: Range | undefined) => {
      const root = scrollRef.current;
      if (!root || !range) return;
      const rect = range.getBoundingClientRect();
      if (!rect.width && !rect.height) return; // 교체돼 떨어져 나간 노드
      const rootRect = root.getBoundingClientRect();
      const top = root.scrollTop + rect.top - rootRect.top - root.clientHeight * 0.3;
      root.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    },
    [scrollRef]
  );

  // 새 검색어 → 다시 찾고 첫 매치로 이동(브라우저 찾기·대화 검색과 같은 동작)
  useEffect(() => {
    const root = contentRef.current;
    const next = root ? runPlans(root, plans) : EMPTY;
    setState(next);
    setCurrent(0);
    scrollToRange(next.hits[0]);
  }, [plans, contentRef, scrollToRange]);

  // 검색 중에 본문이 다시 렌더되면(첨부 정책·차단 목록이 늦게 도착) Range가 떨어져 나가므로 다시 찾는다.
  // 사용자가 보던 위치는 그대로 두고 스크롤하지 않는다.
  useEffect(() => {
    const root = contentRef.current;
    if (!root || !plans.length) return;
    let timer = 0;
    const observer = new MutationObserver(() => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        const next = runPlans(root, plans);
        setState(next);
        setCurrent((c) => Math.min(c, Math.max(0, next.hits.length - 1)));
      }, MUTATION_DEBOUNCE_MS);
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, [plans, contentRef]);

  useEffect(() => {
    paintHighlight(HL_ALL, state.hits);
    const cur = state.hits[current];
    paintHighlight(HL_CURRENT, cur ? [cur] : [], 1);
  }, [state.hits, current]);

  useEffect(
    () => () => {
      paintHighlight(HL_ALL, []);
      paintHighlight(HL_CURRENT, []);
    },
    []
  );

  const total = state.hits.length;

  const goTo = useCallback(
    (index: number) => {
      if (!total) return;
      const i = ((index % total) + total) % total;
      setCurrent(i);
      scrollToRange(state.hits[i]);
    },
    [total, state.hits, scrollToRange]
  );

  const next = useCallback(() => goTo(current + 1), [goTo, current]);
  const prev = useCallback(() => goTo(current - 1), [goTo, current]);
  const goToResult = useCallback(
    (resultIndex: number) => {
      const r = state.results[resultIndex];
      if (r) goTo(r.firstHit);
    },
    [state.results, goTo]
  );
  /** 현재 매치를 다시 화면에 보여 준다(모바일: 결과 패널을 닫은 뒤) */
  const reveal = useCallback(() => scrollToRange(state.hits[current]), [scrollToRange, state.hits, current]);

  return {
    terms: state.terms,
    results: state.results,
    total,
    truncated: state.truncated,
    current,
    currentResult: total ? state.hitResult[current] ?? -1 : -1,
    /** 입력이 디바운스를 아직 안 지났다 — 이때는 '결과 없음'을 띄우지 않는다(깜빡임) */
    pending: query.trim() !== debounced.trim(),
    next,
    prev,
    goToResult,
    reveal,
  };
}
